import React, { useEffect, useState } from "react";
import { analyticsApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trophy } from "lucide-react";

const COLORS = ["#0047AB", "#4338CA", "#16A34A", "#F59E0B", "#DC2626"];

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { analyticsApi.overview().then(setData); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Workforce Analytics</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Reports</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border shadow-none">
          <div className="p-6 border-b border-border">
            <h3 className="font-display text-lg font-bold tracking-tight">Time-to-Fill Trend</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Days from open to placed · rolling 6 months</p>
          </div>
          <div className="p-6 h-72">
            <ResponsiveContainer>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip />
                <Line type="monotone" dataKey="ttf" stroke="#0047AB" strokeWidth={2} dot={{ r: 4, fill: "#0047AB" }} />
                <Line type="monotone" dataKey="placements" stroke="#4338CA" strokeWidth={2} dot={{ r: 4, fill: "#4338CA" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="border-border shadow-none">
          <div className="p-6 border-b border-border">
            <h3 className="font-display text-lg font-bold tracking-tight">Source Effectiveness</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Candidate origin by count</p>
          </div>
          <div className="p-6 h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.sources} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {data.sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2"><Trophy className="h-5 w-5 text-[#F59E0B]" /> Recruiter Leaderboard</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Placements and revenue this quarter</p>
          </div>
          <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider">Q1 2026</Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rank</TableHead>
              <TableHead>Recruiter</TableHead>
              <TableHead className="text-right">Placements</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.leaderboard.map((r, i) => (
              <TableRow key={r.name}>
                <TableCell className="font-mono font-bold">#{i + 1}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right font-mono">{r.placements}</TableCell>
                <TableCell className="text-right font-mono">${(r.revenue / 1000).toFixed(1)}k</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
